import { ComponentType, ElementType, lazy, Suspense } from 'react';
import { SuspenseLoadingScreen } from 'src/components/loading-screen/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: Record<string, unknown>) =>
  (
    <Suspense fallback={<SuspenseLoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );

// ----------------------------------------------------------------------

// Retry lazy loading when an error occurs
export const retryLazy = (componentImport: () => Promise<{ default: ComponentType }>) =>
  lazy(async () => {
    const pageAlreadyRefreshed = JSON.parse(
      window.localStorage.getItem('pageRefreshed') || 'false',
    );
    try {
      const component = await componentImport();
      window.localStorage.setItem('pageRefreshed', 'false');
      return component;
    } catch (error) {
      if (!pageAlreadyRefreshed) {
        window.localStorage.setItem('pageRefreshed', 'true');
        window.location.reload();
        // reload가 완료되기 전까지 타입스크립트를 만족시키기 위해 throw
        throw new Error('Reloading page');
      }
      throw error;
    }
  });

export const Page403 = Loadable(lazy(() => import('../screens/common/Page403')));
export const Page404 = Loadable(lazy(() => import('../screens/common/Page404')));
export const Page500 = Loadable(lazy(() => import('../screens/common/Page500')));
export const HomeV2 = Loadable(lazy(() => import('../screens/home/HomeV2')));
export const PageError = Loadable(retryLazy(() => import('../screens/common/PageError')));
export const Login = Loadable(lazy(() => import('../screens/auth/login/Login')));
export const FindId = Loadable(lazy(() => import('../screens/auth/find-id/FindId')));
export const FindIdEnd = Loadable(lazy(() => import('../screens/auth/find-id/FindIdEnd')));
export const ResetPwd = Loadable(lazy(() => import('../screens/auth/reset-pwd/ResetPwd')));
export const ResetPwdForm = Loadable(lazy(() => import('../screens/auth/reset-pwd/ResetPwdForm')));
export const ResetPwdEnd = Loadable(lazy(() => import('../screens/auth/reset-pwd/ResetPwdEnd')));
export const Register = Loadable(lazy(() => import('../screens/auth/register/Register')));
export const ExistAccount = Loadable(
  lazy(() => import('../screens/auth/register/exist-account/ExistAccount')),
);
export const Verification = Loadable(
  lazy(() => import('../screens/auth/register/verification/Verification')),
);
export const RegisterForm = Loadable(
  lazy(() => import('../screens/auth/register/register-form/RegisterForm')),
);
export const RegisterEnd = Loadable(lazy(() => import('../screens/auth/register/RegisterEnd')));
export const QuitSmokingDateSettings = Loadable(
  lazy(() => import('../screens/quit-smoking-date-settings/QuitSmokingDateSettings')),
);
export const Ftnd = Loadable(lazy(() => import('../screens/ftnd/Ftnd')));
export const FtndForm = Loadable(lazy(() => import('../screens/ftnd/FtndForm')));
export const FtndEnd = Loadable(lazy(() => import('../screens/ftnd/FtndEnd')));
export const MedicalQuestion = Loadable(
  lazy(() => import('../screens/medical-question/MedicalQuestion')),
);
export const MedicalQuestionForm = Loadable(
  lazy(() => import('../screens/medical-question/MedicalQuestionForm')),
);
export const MedicalQuestionEnd = Loadable(
  lazy(() => import('../screens/medical-question/MedicalQuestionEnd')),
);
export const MedicineSettings = Loadable(
  lazy(() => import('../screens/medicine-settings/MedicineSettings')),
);
export const MyPage = Loadable(lazy(() => import('../screens/my-page/MyPage')));
export const EditProfile = Loadable(lazy(() => import('../screens/my-page/my-info/EditProfile')));
export const EditMemberInfo = Loadable(
  lazy(() => import('../screens/my-page/my-info/EditMemberInfo')),
);
export const TermsSettings = Loadable(
  lazy(() => import('../screens/my-page/settings/terms-settings/TermsSettings')),
);
export const Terms = Loadable(lazy(() => import('../screens/terms/Terms')));
export const NotificationSettings = Loadable(
  lazy(() => import('../screens/my-page/settings/notification-settings/NotificationSettings')),
);
export const Faq = Loadable(lazy(() => import('../screens/my-page/customer-service/faq/Faq')));
export const Version = Loadable(
  lazy(() => import('../screens/my-page/customer-service/version/Version')),
);
export const AppGuide = Loadable(
  lazy(() => import('../screens/my-page/customer-service/app-guide/AppGuide')),
);
export const AppGuideDetail = Loadable(
  lazy(() => import('../screens/my-page/customer-service/app-guide/AppGuideDetail')),
);
export const DrJin = Loadable(lazy(() => import('../screens/dr-jin/DrJin')));
export const MissionHistory = Loadable(lazy(() => import('../screens/mission/MissionHistory')));
export const MissionHistoryDetail = Loadable(
  lazy(() => import('../screens/mission/MissionHistoryDetail')),
);
export const MissionDetail = Loadable(lazy(() => import('../screens/mission/MissionDetail')));
export const Craving = Loadable(lazy(() => import('../screens/craving/Craving')));
export const Record = Loadable(lazy(() => import('../screens/craving/record/Record')));
export const RecordEnd = Loadable(lazy(() => import('../screens/craving/record/RecordEnd')));
export const Calendar = Loadable(lazy(() => import('../screens/calendar/Calendar')));
export const QuitSmokingDiary = Loadable(
  lazy(() => import('../screens/calendar/quit-smoking-diary/QuitSmokingDiary')),
);
export const CravingDiary = Loadable(
  lazy(() => import('../screens/calendar/craving-diary/CravingDiary')),
);
export const QuitSmokingAim = Loadable(
  lazy(() => import('../screens/calendar/quit-smoking-aim/QuitSmokingAim')),
);
export const DrugQuestion = Loadable(lazy(() => import('../screens/drug-question/DrugQuestion')));
export const DrugQuestionForm = Loadable(
  lazy(() => import('../screens/drug-question/DrugQuestionForm')),
);
export const EditQuitSmokingDiary = Loadable(
  lazy(() => import('../screens/calendar/quit-smoking-diary/EditQuitSmokingDiary')),
);
export const Supporter = Loadable(lazy(() => import('../screens/supporter/Supporter')));
export const LanguageSettings = Loadable(
  lazy(() => import('../screens/my-page/settings/language-settings/LanguageSettings')),
);
export const PersonaSettings = Loadable(
  lazy(() => import('../screens/my-page/settings/persona-settings/PersonaSettings')),
);
export const Withdrawal = Loadable(lazy(() => import('../screens/my-page/withdrawal/Withdrawal')));
export const Notification = Loadable(lazy(() => import('../screens/notification/Notification')));
export const SmokingPattern = Loadable(
  lazy(() => import('../screens/smoking-pattern/SmokingPattern')),
);
export const CravingForecastDetail = Loadable(
  lazy(() => import('../screens/craving-forecast/CravingForecastDetail')),
);
export const OrganicGlowDemo = Loadable(
  lazy(() => import('../screens/organic-glow-demo/OrganicGlowDemo')),
);
